// Client-side image prep for uploads: avatars (512² JPEG center crop).

type Src = CanvasImageSource & { width?: number; height?: number };

const dims = (s: Src) => ({
  w: (s as HTMLVideoElement).videoWidth || (s as HTMLImageElement).naturalWidth || (s.width as number) || 0,
  h: (s as HTMLVideoElement).videoHeight || (s as HTMLImageElement).naturalHeight || (s.height as number) || 0,
});

/** Decodes a picked file (EXIF orientation applied). */
export async function decodeImage(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // older WebViews: go through <img>
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return await createImageBitmap(img);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

export function toJpeg(c: HTMLCanvasElement, quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error('Không nén được ảnh'))), 'image/jpeg', quality));
}

/** Center square of `src` drawn into a size×size canvas. */
export function centerSquare(src: Src, size: number): HTMLCanvasElement {
  const { w, h } = dims(src);
  const side = Math.min(w, h);
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, (w - side) / 2, (h - side) / 2, side, side, 0, 0, size, size);
  return c;
}

/** Avatar: 512×512 center crop, JPEG. */
export async function avatarJpeg(file: Blob): Promise<Blob> {
  const bmp = await decodeImage(file);
  try {
    return await toJpeg(centerSquare(bmp, 512), 0.88);
  } finally {
    bmp.close();
  }
}
